import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
type FormData = {
  intent: string;
  neighborhoods: string[];
  bringing: string[];
  timeline: string;
  name: string;
  email: string;
};
export function QuizForm() {
  const screenInit = useScreenInit();
  const [step, setStep] = useState(screenInit.step ?? 0);
  const [formData, setFormData] = useState<FormData>({
    intent: '',
    neighborhoods: [],
    bringing: [],
    timeline: '',
    name: '',
    email: ''
  });
  const totalSteps = 6;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 0));
  const toggleArrayItem = (field: keyof FormData, item: string) => {
    setFormData(prev => {
      const array = prev[field] as string[];
      if (array.includes(item)) {
        return {
          ...prev,
          [field]: array.filter(i => i !== item)
        };
      } else {
        return {
          ...prev,
          [field]: [...array, item]
        };
      }
    });
  };
  const handleSelect = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.intent !== '';
      case 2:
        return formData.neighborhoods.length > 0;
      case 3:
        return formData.bringing.length > 0;
      case 4:
        return formData.timeline !== '';
      case 5:
        return formData.name.trim() !== '' && formData.email.trim() !== '';
      default:
        return true;
    }
  };
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return <div className="text-center py-12" data-id="element-151">
            <p className="text-navy/70 mb-10 text-lg" data-id="element-152">
              Take a quick moment to tell us what you're looking for.
            </p>
            <button onClick={nextStep} className="px-10 py-4 bg-marigold text-navy rounded-full font-medium hover:bg-marigold/90 transition-colors inline-flex items-center gap-2" data-id="element-153">
              Join the community
            </button>
          </div>;
      case 1:
        return <div className="space-y-6" data-id="element-154">
            <h3 className="text-2xl font-serif text-navy mb-8" data-id="element-155">
              What are you looking for?
            </h3>
            <div className="grid gap-4" data-id="element-156">
              {['Buying a home', 'Renting', 'Just exploring the familiar spaces community', 'I want to bring my community or group'].map(option => <button key={option} onClick={() => handleSelect('intent', option)} className={`p-6 rounded-xl border text-left transition-all ${formData.intent === option ? 'border-marigold bg-marigold/5 shadow-sm' : 'border-navy/10 bg-white hover:border-navy/30'}`} data-id="element-157">
                  <div className="flex items-center justify-between" data-id="element-158">
                    <span className="text-lg font-medium text-navy" data-id="element-159">
                      {option}
                    </span>
                    {formData.intent === option && <Check className="text-marigold" data-id="element-160" />}
                  </div>
                </button>)}
            </div>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-161">
            <h3 className="text-2xl font-serif text-navy mb-8" data-id="element-162">
              Which neighborhood interests you?{' '}
              <span className="text-sm font-sans text-navy/50 font-normal ml-2" data-id="element-163">
                (Select all that apply)
              </span>
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-164">
              {['Escandón', 'Daniel Garza', 'San Rafael', 'Roma Norte', 'Open to any'].map(option => <button key={option} onClick={() => toggleArrayItem('neighborhoods', option)} className={`p-5 rounded-xl border text-left transition-all ${formData.neighborhoods.includes(option) ? 'border-marigold bg-marigold/5 shadow-sm' : 'border-navy/10 bg-white hover:border-navy/30'}`} data-id="element-165">
                  <div className="flex items-center justify-between" data-id="element-166">
                    <span className="font-medium text-navy" data-id="element-167">{option}</span>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${formData.neighborhoods.includes(option) ? 'bg-marigold border-marigold' : 'border-navy/20'}`} data-id="element-168">
                      {formData.neighborhoods.includes(option) && <Check size={14} className="text-navy" data-id="element-169" />}
                    </div>
                  </div>
                </button>)}
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-170">
            <h3 className="text-2xl font-serif text-navy mb-8" data-id="element-171">
              Are you thinking of bringing friends or family to live nearby?
            </h3>
            <div className="grid gap-4" data-id="element-172">
              {['Yes, friends', 'Yes, family', 'Yes, my community or group', 'for me and my partner', 'Just me for now'].map(option => <button key={option} onClick={() => toggleArrayItem('bringing', option)} className={`p-5 rounded-xl border text-left transition-all ${formData.bringing.includes(option) ? 'border-marigold bg-marigold/5 shadow-sm' : 'border-navy/10 bg-white hover:border-navy/30'}`} data-id="element-173">
                  <div className="flex items-center justify-between" data-id="element-174">
                    <span className="font-medium text-navy" data-id="element-175">{option}</span>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${formData.bringing.includes(option) ? 'bg-marigold border-marigold' : 'border-navy/20'}`} data-id="element-176">
                      {formData.bringing.includes(option) && <Check size={14} className="text-navy" data-id="element-177" />}
                    </div>
                  </div>
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-178">
            <h3 className="text-2xl font-serif text-navy mb-8" data-id="element-179">
              How far along are you?
            </h3>
            <div className="grid gap-4" data-id="element-180">
              {['Just curious', 'Talking about it with people', 'Ready to join the community'].map(option => <button key={option} onClick={() => handleSelect('timeline', option)} className={`p-6 rounded-xl border text-left transition-all ${formData.timeline === option ? 'border-marigold bg-marigold/5 shadow-sm' : 'border-navy/10 bg-white hover:border-navy/30'}`} data-id="element-181">
                  <div className="flex items-center justify-between" data-id="element-182">
                    <span className="text-lg font-medium text-navy" data-id="element-183">
                      {option}
                    </span>
                    {formData.timeline === option && <Check className="text-marigold" data-id="element-184" />}
                  </div>
                </button>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-8" data-id="element-185">
            <h3 className="text-2xl font-serif text-navy mb-4" data-id="element-186">
              Almost done. Where should we send updates?
            </h3>
            <div className="space-y-4" data-id="element-187">
              <div className="space-y-2" data-id="element-188">
                <label className="text-sm font-medium text-navy" data-id="element-189">Name</label>
                <input type="text" value={formData.name} onChange={e => handleSelect('name', e.target.value)} className="w-full px-4 py-4 rounded-xl border border-navy/20 bg-white focus:outline-none focus:ring-2 focus:ring-marigold/50" placeholder="Jane Doe" data-id="element-190" />
              </div>
              <div className="space-y-2" data-id="element-191">
                <label className="text-sm font-medium text-navy" data-id="element-192">Email</label>
                <input type="email" value={formData.email} onChange={e => handleSelect('email', e.target.value)} className="w-full px-4 py-4 rounded-xl border border-navy/20 bg-white focus:outline-none focus:ring-2 focus:ring-marigold/50" placeholder="jane@example.com" data-id="element-193" />
              </div>
            </div>
          </div>;
      case 6:
        return <div className="text-center py-16" data-id="element-194">
            <div className="w-20 h-20 bg-marigold/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-195">
              <Check className="w-10 h-10 text-marigold" data-id="element-196" />
            </div>
            <h3 className="text-3xl font-serif text-navy mb-4" data-id="element-197">
              Gracias — te conectamos con la comunidad.
            </h3>
            <p className="text-navy/70" data-id="element-198">
              We've received your information and will be in touch soon.
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="max-w-2xl mx-auto w-full" data-id="element-199">
      <div className="bg-white rounded-2xl border border-navy/10 shadow-sm overflow-hidden min-h-[500px] flex flex-col" data-id="element-200">
        {/* Progress Bar */}
        {step > 0 && step < totalSteps && <div className="bg-cream/50 px-8 py-4 border-b border-navy/5 flex items-center justify-between" data-id="element-201">
            <span className="text-xs font-medium text-navy/50 uppercase tracking-wider" data-id="element-202">
              Step {step} of {totalSteps - 1}
            </span>
            <div className="flex gap-1" data-id="element-203">
              {Array.from({
            length: totalSteps - 1
          }).map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i < step ? 'bg-marigold w-6' : 'bg-navy/10 w-2'}`} data-id="element-204" />)}
            </div>
          </div>}

        {/* Content Area */}
        <div className="p-8 md:p-12 flex-1 relative flex items-center justify-center" data-id="element-205">
          <AnimatePresence mode="wait" data-id="element-206">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="w-full" data-id="element-207">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Navigation */}
        {step > 0 && step < totalSteps && <div className="p-8 bg-cream/30 border-t border-navy/5 flex items-center justify-between" data-id="element-208">
            <button onClick={prevStep} className="p-3 rounded-full hover:bg-navy/5 text-navy/60 transition-colors" aria-label="Previous step" data-id="element-209">
              <ArrowLeft size={20} data-id="element-210" />
            </button>

            {step === totalSteps - 1 ? <button onClick={nextStep} disabled={!isStepValid()} className="px-8 py-3 bg-marigold text-navy rounded-full font-medium hover:bg-marigold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-211">
                Te conectamos con la comunidad
              </button> : <button onClick={nextStep} disabled={!isStepValid()} className="px-8 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-212">
                Next <ArrowRight size={16} data-id="element-213" />
              </button>}
          </div>}
      </div>
    </div>;
}